import React, { useEffect, useState } from "react";
import { Loader, Text } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import logger from "../../lib/logger";
import useTokenHook from "../../hooks/context/useTokenHook";
import axios from "axios";
import mondaySdk from "monday-sdk-js";
import {
  columnValuesResync,
  ResyncPayloadInterface,
  ApplyOptimisticUpdate,
  NAME_COLUMN_TYPE,
} from "./resynchronization.interface";
import { resyncParentName } from "../../utils/requests/subitemDiscrepancyRequests";
import { RESYNC_ITEM } from "../../data/constants/endpoints";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { MONDAY_API_VERSION } from "../../services/monday/monday-api-version";
import { formatDate } from "../../utils/unixDateFormatter";
import { formatResyncError } from "./optimisticUpdate";
import type { BoardHeader } from "./DiscrepancyGrid/discrepancyGrid.interface";
import ResyncPreviewTable, {
  ResyncPreview,
  previewActionLabel,
  previewTargets,
} from "./ResyncPreviewTable";
import { boardLabel, joinNames } from "./resyncCopy";

const monday = mondaySdk();
monday.setApiVersion(MONDAY_API_VERSION);

interface SyncLatestModalProps {
  isOpen: boolean;
  onClose: () => void;
  columnType: string | null;
  columnTitle: string | null;
  syncId: string | null;
  itemColumnValues: columnValuesResync[] | null;
  applyOptimisticUpdate: ApplyOptimisticUpdate;
  /** Reconcile the values on screen with what was just written. See SharedColumnsProps. */
  onResynced?: () => void;
  /**
   * Board names for the boards in `itemColumnValues`, keyed the same way (`String(board_id)`).
   * Optional so older callers keep working; without it a board is labelled by its id.
   */
  boards?: BoardHeader[];
}

const URI = process.env.REACT_APP_URI;

/**
 * The before/after table's input, from the per-board values and the activity-log winner.
 *
 * The winner is whichever board's column changed most recently according to Monday's activity
 * log — the same rule the resync endpoint applies — so the table's "Latest change" row is the
 * value that will be written, not a guess.
 */
const toPreview = (
  columnTitle: string,
  values: columnValuesResync[],
  latest: columnValuesResync,
  changedAt: string | null,
  boards: BoardHeader[],
): ResyncPreview => {
  const names = Object.fromEntries(boards.map((board) => [board.boardId, board.boardName]));
  return {
    columnTitle,
    latestBoardId: String(latest.board_id),
    latestChangedAt: changedAt,
    rows: values.map((value) => ({
      boardId: String(value.board_id),
      boardName: boardLabel(names, String(value.board_id)),
      currentText: value.exists === false ? null : (value.text ?? null),
      missing: value.exists === false,
    })),
  };
};

/**
 * The Items tab's single-column resync dialog. Same structure as the Subitems tab's
 * (SubitemResyncModal): a sentence naming the board of the latest change, a before/after table
 * with one row per board, and a button that says what it will do. The two used to differ —
 * this one showed only the winning text and a date, with no board named, and a blank line
 * when the winning value was empty.
 */
const SyncLatestModal = ({
  isOpen,
  onClose,
  columnType,
  columnTitle,
  syncId,
  itemColumnValues,
  applyOptimisticUpdate,
  onResynced,
  boards = [],
}: SyncLatestModalProps) => {
  const { token } = useTokenHook();
  const [loading, setLoading] = useState(false);
  const [changedAt, setChangedAt] = useState<string | null>(null);
  const [resyncPayload, setResyncPayload] = useState<ResyncPayloadInterface | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getLatestUpdate = (itemColumnsWithLogs: columnValuesResync[]) => {
    return itemColumnsWithLogs.reduce((latest: any, current: any) => {
      if (!latest || !latest.last_updated) return current;
      if (!current.last_updated) return latest;
      return parseInt(current.last_updated) > parseInt(latest.last_updated) ? current : latest;
    }, null);
  };

  /**
   * A name row ranks on the item's own `updated_at`, not on a column activity log: a rename
   * is not a column change, so `getColumnUpdateLog` has nothing to find for it. The ISO
   * stamp is rescaled to the activity log's unit so the same `getLatestUpdate` and
   * `formatDate` serve both kinds of row.
   */
  const rankNamesByItemTimestamp = (values: columnValuesResync[]) => {
    const present = values.filter((item) => item.exists !== false);
    const withTimestamps: any[] = present.map((item) => ({
      ...item,
      last_updated: item.updated_at ? String(Date.parse(item.updated_at) * 10000) : null,
    }));
    const mostUpdatedItem = getLatestUpdate(withTimestamps);
    const targetItems = withTimestamps.filter(
      (update: any) => update.item_id !== mostUpdatedItem.item_id && update.exists,
    );
    setChangedAt(mostUpdatedItem.last_updated ? formatDate(mostUpdatedItem.last_updated) : null);
    setResyncPayload({ latestUpdate: mostUpdatedItem, targetItems });
  };

  useEffect(() => {
    const fetchUpdateLogs = async (itemColumnValues: any) => {
      try {
        setLoading(true);
        if (columnType === NAME_COLUMN_TYPE) {
          rankNamesByItemTimestamp(itemColumnValues);
          return;
        }
        const allLogs = await Promise.all(
          itemColumnValues
            .filter((item: columnValuesResync) => item.exists !== false)
            .map(async (item: any) => {
              const { item_id, column_id, board_id } = item;

              const response = await MondayApiQueryInstance.getColumnUpdateLog(
                item_id,
                column_id,
                board_id,
                1,
              );
              return response?.data?.boards[0];
            }),
        );

        //match logs with itemColumnValues and append it as last_updated
        const itemsWithUpdateLogs = itemColumnValues
          .filter((item: columnValuesResync) => item.exists !== false)
          .reduce((acc: any[], item: columnValuesResync) => {
            const matchingLog = allLogs.find((log: any) => log?.id === item.board_id);

            acc.push({
              ...item,
              last_updated: matchingLog?.activity_logs[0]?.created_at || null,
            });

            return acc;
          }, []);

        const mostUpdatedItem = getLatestUpdate(itemsWithUpdateLogs);

        const targetItems =
          itemsWithUpdateLogs.filter(
            (update: columnValuesResync) =>
              update.item_id !== mostUpdatedItem.item_id && update.exists,
          ) || [];

        setChangedAt(formatDate(mostUpdatedItem.last_updated));
        setResyncPayload({ latestUpdate: mostUpdatedItem, targetItems: targetItems });
      } catch (error: any) {
        logger.error("Error fetching update logs:", error);
      } finally {
        setLoading(false);
      }
    };

    if (itemColumnValues && itemColumnValues.length > 0) {
      fetchUpdateLogs(itemColumnValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemColumnValues, columnType]);

  useEffect(() => {
    if (!isOpen) setErrorMessage(null);
  }, [isOpen]);

  const preview =
    resyncPayload && itemColumnValues
      ? toPreview(columnTitle ?? "this column", itemColumnValues, resyncPayload.latestUpdate, changedAt, boards)
      : null;

  const handleConfirmSync = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      if (columnType === NAME_COLUMN_TYPE) {
        // A rename goes down its own write path — `/simb/hard-resync` writes column
        // values and a name is not a column. Every board that holds a name is sent, and
        // the board of the latest change is named as the source, so the backend renames
        // exactly the boards this dialog said it would.
        const namesByBoard = Object.fromEntries(
          [resyncPayload!.latestUpdate, ...resyncPayload!.targetItems].map((entry) => [
            String(entry.board_id),
            { name: entry.text ?? "", updatedAt: entry.updated_at ?? "" },
          ]),
        );
        await resyncParentName(
          token!,
          syncId!,
          namesByBoard,
          String(resyncPayload!.latestUpdate.board_id),
        );
      } else {
        await axios.post(
          `${URI}${RESYNC_ITEM}`,
          {
            syncId: syncId,
            resync_payload: resyncPayload,
            column_type: columnType,
            column_title: columnTitle,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }

      applyOptimisticUpdate(
        syncId!,
        resyncPayload?.targetItems!,
        resyncPayload?.latestUpdate.value!,
        resyncPayload?.latestUpdate.text!,
        columnType!,
        columnTitle!,
        false,
      );

      // Patches the parent's discrepancies entry, which is what the row's badge and state pill
      // read — but NOT the column values the expanded grid renders, which belong to whoever
      // fetched them. Ask that owner to reconcile.
      onResynced?.();

      const updatedOn = preview ? previewTargets(preview).map((row) => row.boardName) : [];
      monday.execute("notice", {
        message:
          updatedOn.length > 0
            ? `${columnTitle ?? "Column"} was updated on ${joinNames(updatedOn)}.`
            : `${columnTitle ?? "Column"} was updated.`,
        type: "success",
        timeout: 10000,
      });

      onClose();
    } catch (error: any) {
      logger.error("Error resyncing latest change:", error);
      setErrorMessage(formatResyncError(error, { columnTitle }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal id="sync-latest" show={isOpen} onClose={onClose} size="medium">
      <ModalBasicLayout>
        <ModalHeader title={`Sync ${columnTitle ?? "column"}`} className="px-8" />
        <ModalContent>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
            {preview ? (
              <>
                <ResyncPreviewTable preview={preview} />
                <Text type="text2" color="secondary" element="p">
                  This cannot be undone.
                </Text>
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-small)" }}>
                <Loader size="small" color="primary" />
                <Text type="text2" color="secondary">
                  Checking which board changed most recently…
                </Text>
              </div>
            )}
            {errorMessage && (
              <div
                role="alert"
                style={{
                  padding: "var(--spacing-small) var(--spacing-medium)",
                  borderRadius: "var(--border-radius-small)",
                  background: "var(--negative-color-selected)",
                }}
              >
                <Text type="text2" element="p">
                  {errorMessage}
                </Text>
              </div>
            )}
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{
          onClick: handleConfirmSync,
          text: preview ? previewActionLabel(preview) : "Confirm",
          loading: loading,
          disabled: !preview,
        }}
        secondaryButton={{
          onClick: onClose,
          text: "Cancel",
          disabled: loading,
        }}
      />
    </Modal>
  );
};

export default SyncLatestModal;
